import { useCallback, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { useMe } from "@/auth/use-me";
import { isMissingEntityError } from "@/lib/graphql-error";
import {
  CompanyContactPersonsDocument,
  CompanyMembersDocument,
  CompanyProfileDocument,
  MyCompanyRolesDocument,
  CompanyCompetenciesDocument,
  CompanyOwnedSharedItemsDocument,
  type CompanyContactPersonsQuery,
  type CompanyMembersQuery,
  type CompanyProfileQuery,
  type CompanyCompetenciesQuery,
  type CompanyOwnedSharedItemsQuery,
} from "@/graphql/generated/operations";
import { COMPANY_ROLE_ADMIN } from "./company-roles";

// Company-aggregate read hook for the company page (spark#221).
//
// The READ half, deliberately: every write lives in use-company-admin, so a component that only
// displays a company cannot reach a mutation from here and there is no busy flag to thread through
// the showcase. The shape otherwise mirrors usePerson so the page reads like ProfilePage to anyone
// who already knows that one.

type CompanyData = NonNullable<CompanyProfileQuery["company"]["company"]>;
type MemberData = CompanyMembersQuery["companyMembers"]["nodes"][number];
type ContactData = CompanyContactPersonsQuery["companyContactPersons"]["contactPersons"][number];
type SharedItemData = CompanyOwnedSharedItemsQuery["companyOwnedSharedItems"]["nodes"][number];
type CompetencyData = CompanyCompetenciesQuery["companyCompetencies"]["competencies"][number];

export interface UseCompanyResult {
  company: CompanyData | null;
  /**
   * The members loaded SO FAR, not the roster. genesis pages this list (@external-adr GENESIS-0029-the-member-strip-is-paged-and-the-contact-strip-is-not), so this grows by
   * a page per `loadMoreMembers` and is complete only once `hasMoreMembers` is false.
   */
  members: MemberData[];
  /**
   * How many members this list can reach — the "Anzahl" of
   * company-profiles/0006-company-page-mvp §2.
   *
   * THE SAME NUMBER FOR EVERY CALLER since concept v5. genesis runs `totalCount` through the same
   * predicate as the page, and that predicate stopped taking a principal when v5 deleted the
   * `CURRENT_EMPLOYER` publication rule (@external-adr GENESIS-0023-company-membership-is-the-per-company-authorization §7 is the record of what it used to do).
   *
   * NOT the company's headcount, and it must never be worded as one: it counts active people holding
   * a membership row. The company's own stated size is `company.employeeCount`, a different fact.
   */
  memberCount: number;
  /** Whether genesis has another page of members beyond the ones in `members`. */
  hasMoreMembers: boolean;
  /** Appends the next page to `members`. A no-op while one is already in flight or none is left. */
  loadMoreMembers: () => void;
  loadingMoreMembers: boolean;
  /**
   * A failed `loadMoreMembers`, kept OUT of `error` on purpose: the page loaded, one more page did
   * not, and turning that into the page-level failure state would replace a rendered company with
   * an error screen. The strip says so neutrally and the control stays clickable.
   */
  moreMembersError: Error | null;
  /**
   * The company's contact persons (genesis ADR company-profiles/0006-company-page-mvp §4), at most
   * ten since concept v13, complete.
   *
   * ITS OWN READ SINCE @external-adr GENESIS-0029-the-member-strip-is-paged-and-the-contact-strip-is-not, and it had to become one. It used to be `members` filtered
   * on `contactPerson`, which was sound only while that list was the whole roster: once the roster
   * is paged, the same filter quietly answers "the contacts on page 1" — page one might carry two
   * of the marked people and page two none.
   *
   * What made the old filter safe was never that it was one query. It was that contacts INHERITED the
   * roster's rule instead of restating it, and genesis keeps exactly that property by construction:
   * `companyContactPersons` is the `companyMembers` predicate with `contact_person` ANDed on and a
   * LIMIT at the cap — one implementation, two callers. So this is not the "separate unfiltered read"
   * the old note ruled out; that read still must not be built.
   *
   * The rule being inherited is no longer the per-caller @external-adr GENESIS-0023-company-membership-is-the-per-company-authorization §7 publication rule: concept v5
   * deleted it, and the roster is the same for every caller now. Sharing the predicate matters for a
   * plainer reason since — the two can never disagree about who is on the list.
   */
  contactPersons: ContactData[];
  /** The posts and projects the COMPANY OWNS (0006 §5, v7) — not its members' personal items, which
   *  stay on their own profiles. The derived, membership-based projection this used to describe was
   *  retired with v7: the page shows what the company published, not a digest of its staff. */
  sharedItems: SharedItemData[];
  /**
   * The expertise, strongest first. Free text owned by this company since @external-adr GENESIS-0031-company-expertise-is-per-company-free-text — the
   * label is the identity, and the same word at another company means nothing in common. Contains
   * ONLY listed labels: an absent one has not been assessed, which is not the same as a degree of 0
   * and must never be rendered as one.
   */
  competencies: CompetencyData[];
  /** Presentation only — see the note where it is built. */
  viewerIsAdmin: boolean;
  loading: boolean;
  notFound: boolean;
  /**
   * A genuine failure worth reporting — never the "no such company" answer, which `notFound` already
   * carries. See the note where it is built.
   */
  error: Error | null;
  refetch: () => void;
}

/** One page of work on the company page. The page is a showcase, not a feed. */
const ITEM_PAGE_SIZE = 12;

/**
 * One page of the member strip — company-profiles/0006-company-page-mvp §2 asks for a *compact*
 * avatar row, so the first screenful is a screenful and the rest is asked for.
 *
 * Stated rather than left to genesis's default (20), and well under its cap of 100: above the cap
 * genesis REFUSES the request (GENESIS001009) instead of trimming it, so a page size is a thing to
 * choose deliberately, not to push at.
 */
export const MEMBER_PAGE_SIZE = 12;

export function useCompany(companyId: string): UseCompanyResult {
  const me = useMe();
  const [loadingMoreMembers, setLoadingMoreMembers] = useState(false);
  const [moreMembersError, setMoreMembersError] = useState<Error | null>(null);

  const companyQuery = useQuery(CompanyProfileDocument, {
    variables: { input: { id: companyId } },
    skip: !companyId,
  });
  const membersQuery = useQuery(CompanyMembersDocument, {
    variables: { input: { companyId, first: MEMBER_PAGE_SIZE } },
    skip: !companyId,
  });
  // The contact persons, separately and unpaged — see the note on `contactPersons` above for why
  // this is not the second read @external-adr GENESIS-0023-company-membership-is-the-per-company-authorization §7 warns about.
  const contactsQuery = useQuery(CompanyContactPersonsDocument, {
    variables: { input: { companyId } },
    skip: !companyId,
  });
  const competenciesQuery = useQuery(CompanyCompetenciesDocument, {
    variables: { input: { companyId } },
    skip: !companyId,
  });
  // The viewer's own membership, read directly rather than derived from the roster — see the note on
  // `viewerIsAdmin`. Skipped for a signed-out visitor: myCompanies needs an identity, and an
  // anonymous reader administers nothing anyway.
  const myRolesQuery = useQuery(MyCompanyRolesDocument, {
    skip: !companyId || !me.id,
  });
  const itemsQuery = useQuery(CompanyOwnedSharedItemsDocument, {
    // A showcase, not a feed: one page of the most recent work, no infinite scroll. genesis applies
    // the company scope on top of the ordinary shared-item query.
    //
    // IT CAN SURFACE MORE THAN /share DOES, and deliberately: an INACTIVE company's items are gone
    // from both searches and still listed here, because v9 takes a dormant company's content out of
    // SEARCH rather than off its own page (0005 §2, 0006 §5).
    variables: { companyId, input: { first: ITEM_PAGE_SIZE } },
    skip: !companyId,
  });

  const company = companyQuery.data?.company.company ?? null;
  const membership = membersQuery.data?.companyMembers;
  const members = membership?.nodes ?? [];
  const contactPersons = contactsQuery.data?.companyContactPersons.contactPersons ?? [];
  const sharedItems = itemsQuery.data?.companyOwnedSharedItems.nodes ?? [];
  const competencies = competenciesQuery.data?.companyCompetencies.competencies ?? [];

  const endCursor = membership?.pageInfo.endCursor ?? null;
  // genesis derives this from a limit+1 excess-row probe over the SAME predicate as the page, so it
  // answers about members this caller may see and not about rows — a page of N is N people.
  const hasMoreMembers = membership?.pageInfo.hasNextPage ?? false;

  const fetchMoreMembers = membersQuery.fetchMore;
  const loadMoreMembers = useCallback(() => {
    if (!hasMoreMembers || endCursor == null || loadingMoreMembers) return;
    setLoadingMoreMembers(true);
    setMoreMembersError(null);
    // Accumulated by the `companyMembers` field policy in graphql/client, keyed on companyId alone
    // so the pages of one roster fold into one cache entry and two companies never share one.
    fetchMoreMembers({
      variables: { input: { companyId, first: MEMBER_PAGE_SIZE, after: endCursor } },
    })
      .catch((cause: unknown) => {
        setMoreMembersError(cause instanceof Error ? cause : new Error(String(cause)));
      })
      .finally(() => setLoadingMoreMembers(false));
  }, [companyId, endCursor, fetchMoreMembers, hasMoreMembers, loadingMoreMembers]);

  // Derived from the membership row, deliberately NOT from a Keycloak role. @external-adr GENESIS-0023-company-membership-is-the-per-company-authorization: the
  // platform role create:company is held by everyone allowed to register a company, so gating on
  // it would both hide the affordance from ordinary company admins and show it to strangers.
  //
  // Read off the pages LOADED SO FAR, which is enough because genesis orders the roster admins-first
  // (`ROLE ASC, PERSON_ID ASC` — @external-adr GENESIS-0029-the-member-strip-is-paged-and-the-contact-strip-is-not decision's keyset), so the caller's own admin row is on page one
  // unless the company has more admins than one page holds. A miss there is a false negative that
  // costs an admin the edit button until they load another page; it can never be a false positive,
  // because a row saying `admin` about the caller is exactly what genesis's own gate reads.
  //
  // READ FROM THE VIEWER'S OWN MEMBERSHIP, NOT FROM THE ROSTER.
  //
  // This used to scan the loaded `members` for the viewer's row. That is a partial list: the roster
  // pages MEMBER_PAGE_SIZE at a time, so an admin whose row had not been fetched yet was told they
  // were not an admin and saw no edit controls until they happened to page far enough
  // (jfalkenstein, !238 note_25762). genesis orders the roster by role before person id and `admin`
  // sorts before `member`, so it took more than a page of admins in one company to bite — rare, but
  // the answer was still being derived from something that does not carry it.
  //
  // An unrecognised role degrades to "not an admin": `role` is an open string (@adr SPARK-0002-company-account-model@1 §4) and
  // company-roles.ts is explicit that these are the values spark KNOWS, not the values that exist.
  //
  // This is presentation only. genesis re-checks the row on every mutation and does not trust
  // anything the client believes, so hiding a button here is a courtesy, never a control.
  const viewerIsAdmin = (myRolesQuery.data?.myCompanies.memberships ?? []).some(
    (m) => m.company.id === companyId && m.role === COMPANY_ROLE_ADMIN,
  );

  // A BAD /c/:id ARRIVES AS AN ERROR, NOT AS A NULL, so it has to be recognised here or it is
  // reported as a fault. genesis parses the URL segment with parseUUID — InvalidIdentifierException
  // for anything that is not a UUID — and reads the row with orElseThrow(NoSuchEntityException)
  // (CompanyController.company). Both say the same thing `notFound` already says, and a mistyped or
  // truncated link is the ordinary case on a route driven straight from the address bar.
  //
  // Answering it with "Something went wrong (Code …)" would be wrong twice: nothing went wrong, and
  // a support code spent on a typo teaches the reader that support codes are noise — @adr SPARK-0010-no-raw-server-errors-in-ui@1 keeps
  // them for failures somebody could actually investigate. Filtered per query rather than picked
  // off the front of the chain, so a real failure in one read still surfaces when another read is
  // merely answering "no such company".
  const failure =
    [
      companyQuery.error,
      membersQuery.error,
      contactsQuery.error,
      itemsQuery.error,
      competenciesQuery.error,
    ]
      .filter((each): each is Error => each != null)
      .find((each) => !isMissingEntityError(each)) ?? null;

  return {
    company,
    members,
    memberCount: membership?.totalCount ?? 0,
    hasMoreMembers,
    loadMoreMembers,
    loadingMoreMembers,
    moreMembersError,
    contactPersons,
    sharedItems,
    competencies,
    viewerIsAdmin,
    // `&& !data` matters: the app-wide fetchPolicy is cache-and-network, so bare `loading` would
    // repaint a loading screen over perfectly good data on every revisit.
    loading: companyQuery.loading && !companyQuery.data,
    // CompanyResult.company is nullable, and genesis answers null for an unknown id and for one the
    // caller may not read alike — so this cannot distinguish them and the page must not imply it can.
    notFound: !companyQuery.loading && !company,
    error: failure,
    refetch: () => {
      void companyQuery.refetch();
      // Back to page one, deliberately: the field policy replaces on a cursor-less read, so a
      // refetch cannot leave stale pages appended behind a fresh first page.
      void membersQuery.refetch();
      void contactsQuery.refetch();
      void itemsQuery.refetch();
      void competenciesQuery.refetch();
    },
  };
}
